import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NoRecordsFound from "components/Records/NoRecordsFound";

export interface SimpleListItem {
  id: number | string;
  primary: string;
  secondary?: string;
  __group?: string;
}

interface SimpleListProps<T extends SimpleListItem> {
  items: T[];
  selected?: (number | string)[];
  onToggleSelect?: (id: number | string) => void;
  showCheckbox?: boolean;

  /**
   * Optional override if you want a custom highlight color.
   * If omitted, uses theme.palette.primary.main.
   */
  primaryColor?: string;

  emptyMessage?: string;
}

const SimpleList = <T extends SimpleListItem>({
  items,
  selected = [],
  onToggleSelect,
  showCheckbox = true,
  primaryColor,
}: SimpleListProps<T>) => {
  const theme = useTheme();
  const highlight = primaryColor ?? theme.palette.primary.main;

  if (items.length === 0) {
    return (
      <NoRecordsFound
        itemName="records"
        minHeight={50}
        maxHeight={100}
      />
    );
  }

  return (
    <List>
      {items.map((item) => {
        const isSecondary = item.__group === "secondary";

        return (
          <ListItem
            key={item.id}
            disablePadding
            sx={{
              opacity: isSecondary ? 0.5 : 1,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            {showCheckbox && onToggleSelect && (
              <Checkbox
                edge="start"
                checked={selected.includes(item.id)}
                onChange={() => onToggleSelect(item.id)}
                color="primary"
                sx={{
                  mr: 1,
                  // If a caller supplies a custom primaryColor, reflect it here.
                  ...(primaryColor
                    ? { "&.Mui-checked": { color: highlight } }
                    : null),
                }}
              />
            )}

            <ListItemText
              primary={item.primary}
              secondary={item.secondary}
              slotProps={{
                primary: { sx: { color: theme.palette.text.primary } },
                secondary: { sx: { color: theme.palette.text.secondary } },
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

export default SimpleList;
